import type {
  Block,
  ContactBlock,
  CtaBlock,
  FeaturesBlock,
  HeroBlock,
  LogosBlock,
  TeamBlock,
  Link as LinkT,
  PricingBlock,
  SplitBlock,
  TextBlock,
} from '@publiqa/shared'
import { RichText, lines } from '../../lib/richtext'
import { SmartLink, useLinkState } from '../context'
import { Picture } from '../ui'
import { CarouselView } from './Carousel'
import { FormView } from './Form'
import { RegistrationView } from './Registration'
import { NewsView } from './News'
import { HeroSlides } from './HeroSlides'
import { HeadlineView, MostReadView, NewsletterView, SectionView } from './Journal'
import { GroupsView } from './Groups'
import { PlanningView } from './Planning'
import { SitemapView } from '../Legal'

/**
 * Bouton de bloc. S'il mène à un bloc masqué, il disparaît — ou, avec
 * `closedCard`, laisse place au message de fermeture du bulletin d'inscription.
 */
function Button({
  link,
  variant,
  closedCard = false,
  hideWhenClosed = false,
}: {
  link: LinkT | null
  variant: 'primary' | 'ghost' | 'light'
  closedCard?: boolean
  /** Masqué aussi s'il mène à une section qui renvoie au bulletin fermé (tarifs). */
  hideWhenClosed?: boolean
}) {
  const state = useLinkState()
  if (!link?.label) return null
  const { hidden, closedMessage, leadsToClosed } = state(link.href)
  if (hideWhenClosed && leadsToClosed) return null
  if (hidden) {
    return closedCard && closedMessage ? (
      <div className="closed-card" role="status">
        {closedMessage}
      </div>
    ) : null
  }
  return (
    <SmartLink href={link.href || '#'} className={`btn btn-${variant}`}>
      {link.label}
    </SmartLink>
  )
}

function Hero({ b }: { b: HeroBlock }) {
  const slides = b.slides?.length ? b.slides : []
  return (
    <section
      className={`b-hero h-${b.height} a-${b.align}${slides.length > 1 ? ' has-slides' : ''}`}
    >
      {slides.length > 1 ? (
        <HeroSlides images={slides} interval={b.interval ?? 6} label={b.title || 'Diaporama'} />
      ) : (
        <Picture image={slides[0] ?? b.image} className="b-hero-bg" eager />
      )}
      <div className="b-hero-shade" style={{ opacity: b.overlay / 100 }} />
      <div className="container b-hero-inner">
        {b.eyebrow && <p className="eyebrow">{b.eyebrow}</p>}
        <h1>{b.title}</h1>
        {b.subtitle && <p className="lead">{b.subtitle}</p>}
        {(b.primary || b.secondary) && (
          <div className="actions">
            <Button link={b.primary} variant="primary" />
            <Button link={b.secondary} variant="light" />
          </div>
        )}
      </div>
    </section>
  )
}

function Text({ b }: { b: TextBlock }) {
  return (
    <section className={`b-section b-text a-${b.align}`}>
      <div className="container narrow">
        {b.title && <h2 className="section-title">{b.title}</h2>}
        <RichText text={b.body} className="prose" />
      </div>
    </section>
  )
}

function Split({ b }: { b: SplitBlock }) {
  return (
    <section className={`b-section b-split img-${b.imageSide}`}>
      <div className="container b-split-grid">
        <div className="b-split-media">
          <Picture image={b.image} label={b.title} />
        </div>
        <div className="b-split-text">
          {b.eyebrow && <p className="eyebrow">{b.eyebrow}</p>}
          {b.title && <h2 className="section-title">{b.title}</h2>}
          <RichText text={b.body} className="prose" />
          {b.cta && (
            <div className="actions">
              <Button link={b.cta} variant="primary" closedCard />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * Tuiles photo (style « vitrine produits ») : photo plein cadre, titre et
 * texte en surimpression sur un dégradé. Sans photo : aplat aux couleurs.
 */
function FeatureTiles({ b }: { b: FeaturesBlock }) {
  return (
    <section className="b-section b-features b-tiles">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />
        <div className={`tiles cols-${b.columns}`}>
          {b.items.map((item, i) => (
            <article className="tile" key={i}>
              <Picture image={item.image} alt={item.title} className="tile-img" />
              <div className="tile-body">
                {item.icon && (
                  <span className="tile-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <h3>{item.title}</h3>
                {item.text && <p>{item.text}</p>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function Features({ b }: { b: FeaturesBlock }) {
  if (b.style === 'tiles') return <FeatureTiles b={b} />
  return (
    <section className="b-section b-features">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />
        <div className={`grid cols-${b.columns}`}>
          {b.items.map((item, i) => (
            <article className={`feature${item.image ? ' has-img' : ''}`} key={i}>
              {item.image && (
                <div className="feature-media">
                  <Picture image={item.image} alt={item.title} />
                </div>
              )}
              <div className="feature-body">
                {item.icon && (
                  <span className="feature-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function Pricing({ b }: { b: PricingBlock }) {
  return (
    <section className="b-section b-pricing">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />
        <div
          className={`plans plans-${Math.min(Math.max(b.plans.length, 1), 5)}${b.plans.length >= 4 ? ' plans-compact' : ''}`}
        >
          {b.plans.map((plan, i) => (
            <div className={`plan${plan.highlight ? ' plan-hl' : ''}`} key={i}>
              {plan.highlight && (b.badgeLabel ?? 'Recommandé') && (
                <span className="plan-badge">{b.badgeLabel ?? 'Recommandé'}</span>
              )}
              <h3>{plan.name}</h3>
              <p className="plan-price">
                <strong>{plan.price}</strong> <span>{plan.period}</span>
              </p>
              <ul>
                {lines(plan.features).map((f, j) => (
                  <li key={j}>{f}</li>
                ))}
              </ul>
              <Button
                link={plan.cta}
                variant={plan.highlight ? 'primary' : 'ghost'}
                hideWhenClosed
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Cta({ b }: { b: CtaBlock }) {
  return (
    <section className={`b-cta tone-${b.tone}`}>
      <div className="container b-cta-inner">
        <div>
          <h2>{b.title}</h2>
          {b.text && <p>{b.text}</p>}
        </div>
        <Button link={b.cta} variant={b.tone === 'light' ? 'primary' : 'light'} closedCard />
      </div>
    </section>
  )
}

/** Paragraphes séparés par une ligne vide ; « Libellé : » en tête devient un intertitre. */
function addressParts(text: string) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const [first = '', ...rest] = p.split('\n')
      return first.trim().endsWith(':') && rest.length
        ? { label: first.trim().replace(/\s*:$/, ''), body: rest.join('\n') }
        : { label: '', body: p }
    })
}

const CONTACT_ICONS = {
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  phone: (
    <path d="M5 4h3.5l1.5 4.5-2.2 1.4a11 11 0 0 0 6.3 6.3l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4z" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
}

function ContactIcon({ name }: { name: keyof typeof CONTACT_ICONS }) {
  return (
    <span className="ct-ico" aria-hidden="true">
      <svg viewBox="0 0 24 24">{CONTACT_ICONS[name]}</svg>
    </span>
  )
}

/**
 * Coordonnées : une seule carte sur toute la largeur — une colonne par
 * adresse (avec itinéraire), puis téléphone / e-mail et horaires.
 */
function Contact({ b }: { b: ContactBlock }) {
  const addresses = b.address ? addressParts(b.address) : []
  const maps = (body: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(body.replace(/\n/g, ', '))}`
  return (
    <section className="b-section b-contact">
      <div className="container">
        {b.title && <h2 className="section-title">{b.title}</h2>}
        <div className="ct-card">
          <div className="ct-items">
            {addresses.map((a, i) => (
              <div key={i} className="ct-item">
                <ContactIcon name="pin" />
                <div>
                  <h3>{a.label || b.addressLabel || 'Adresse'}</h3>
                  <p className="pre">{a.body}</p>
                  <a className="ct-link" href={maps(a.body)} target="_blank" rel="noreferrer">
                    Itinéraire <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </div>
            ))}
            {(b.phone || b.email) && (
              <div className="ct-item">
                <ContactIcon name={b.email ? 'mail' : 'phone'} />
                <div>
                  <h3>{b.contactLabel || 'Contact'}</h3>
                  {b.phone && (
                    <p>
                      <a href={`tel:${b.phone.replace(/\s/g, '')}`}>{b.phone}</a>
                    </p>
                  )}
                  {b.email && (
                    <p>
                      <a href={`mailto:${b.email}`}>{b.email}</a>
                    </p>
                  )}
                  {b.email && (
                    <a className="ct-link" href={`mailto:${b.email}`}>
                      Envoyer un e-mail <span aria-hidden="true">→</span>
                    </a>
                  )}
                </div>
              </div>
            )}
            {b.hours && (
              <div className="ct-item">
                <ContactIcon name="clock" />
                <div>
                  <h3>{b.hoursLabel || 'Horaires'}</h3>
                  {lines(b.hours).map((l, i) => (
                    <p key={i}>{l}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function Logos({ b }: { b: LogosBlock }) {
  if (!b.items.length) return null
  return (
    <section className="b-section b-logos">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />
        <ul className="logos">
          {b.items.map((item, i) => {
            const content = item.image ? (
              <img src={item.image.url} alt={item.image.alt || item.name} loading="lazy" />
            ) : (
              <span className="logo-name">{item.name}</span>
            )
            return (
              <li key={i} title={item.name || undefined}>
                {item.href ? (
                  <SmartLink href={item.href} aria-label={item.name || undefined}>
                    {content}
                  </SmartLink>
                ) : (
                  content
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

/** Initiales, quand un membre n'a pas (encore) de photo. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')
}

function Team({ b }: { b: TeamBlock }) {
  if (!b.members.length) return null
  return (
    <section className="b-section b-team">
      <div className="container">
        <SectionHead title={b.title} intro={b.intro} />
        <ul className="team">
          {b.members.map((m, i) => (
            <li key={i} className="member">
              {m.photo ? (
                <img
                  className="member-photo"
                  src={m.photo.url}
                  alt={m.photo.alt || m.name}
                  loading="lazy"
                />
              ) : (
                <span className="member-photo member-initials" aria-hidden="true">
                  {initials(m.name) || '?'}
                </span>
              )}
              <h3>{m.name}</h3>
              {m.role && <p className="member-role">{m.role}</p>}
              {m.text && <p className="member-text">{m.text}</p>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

export function SectionHead({ title, intro }: { title: string; intro?: string }) {
  if (!title && !intro) return null
  return (
    <header className="section-head">
      {title && <h2 className="section-title">{title}</h2>}
      {intro && <p className="section-intro">{intro}</p>}
    </header>
  )
}

export function BlockView({ block, pageId }: { block: Block; pageId: string }) {
  switch (block.type) {
    case 'hero':
      return <Hero b={block} />
    case 'text':
      return <Text b={block} />
    case 'split':
      return <Split b={block} />
    case 'features':
      return <Features b={block} />
    case 'news':
      return <NewsView b={block} />
    case 'carousel':
      return <CarouselView b={block} />
    case 'pricing':
      return <Pricing b={block} />
    case 'cta':
      return <Cta b={block} />
    case 'form':
      return <FormView b={block} pageId={pageId} />
    case 'contact':
      return <Contact b={block} />
    case 'logos':
      return <Logos b={block} />
    case 'team':
      return <Team b={block} />
    case 'registration':
      return <RegistrationView b={block} pageId={pageId} />
    case 'groups':
      return <GroupsView b={block} />
    case 'planning':
      return <PlanningView b={block} />
    case 'sitemap':
      return <SitemapView b={block} />
    case 'headline':
      return <HeadlineView b={block} />
    case 'section':
      return <SectionView b={block} />
    case 'mostread':
      return <MostReadView b={block} />
    case 'newsletter':
      return <NewsletterView b={block} />
  }
}

export function Blocks({ blocks, pageId }: { blocks: Block[]; pageId: string }) {
  return (
    <>
      {blocks
        .filter((b) => !b.hidden)
        .map((b) => (
          <div key={b.id} id={b.id} className={`block block-${b.type}`}>
            <BlockView block={b} pageId={pageId} />
          </div>
        ))}
    </>
  )
}
